"use client";

import { useEffect, useContext, useState, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Autocomplete,
  IconButton,
  Card,
  CardContent,
  Divider,
  Menu,
  MenuItem,
  alpha,
  Tooltip,
  Badge,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  Area,
  AreaChart,
} from "recharts";
import { ErpContext } from "../../context/ErpContext.js";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import ModalDetalleCuentaCorriente from "./graficas/modal-detalle-cuenta-corriente";
import GraficasCuentaCorrienteModal from "./graficas/graficas-cuenta-corriente-modal";
import CustomPagination from "./CustomPagination";
import ModalMedioDePago from "./ModalMedioDePago.js";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import BalanceIcon from "@mui/icons-material/Balance";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import ReceiptIcon from "@mui/icons-material/Receipt";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PrintIcon from "@mui/icons-material/Print";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PaymentsIcon from '@mui/icons-material/Payments';

import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import BarChartIcon from "@mui/icons-material/BarChart";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Definir el tema personalizado
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "#f4b10e", // Color amarillo/naranja
    },
    success: {
      main: "#4CAF50",
    },
    error: {
      main: "#F44336",
    },
    warning: {
      main: "#FF8042",
    },
    info: {
      main: "#0088FE",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "visible",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#5b5b5f",
            color: "white",
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #f0f0f0",
          },
          "& .MuiDataGrid-row": {
            "&:nth-of-type(even)": {
              backgroundColor: "#f9f9f9",
            },
            "&:hover": {
              backgroundColor: "#f0f4fa",
            },
          },
        },
      },
    },
  },
});

// Componente para el gradiente de área
function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.8} />
        <stop offset="95%" stopColor={color} stopOpacity={0.1} />
      </linearGradient>
    </defs>
  );
}

// Componente principal
const CuentasCorrientesTable = () => {
  const {
    cuentaCorrienteTransformed,
    getCuentaCorrienteTransformed,
    isNavbarCollapsed,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    getUbicacion,
    ubicaciones,
    getNegocio,
    negocios,
    getTransaccionItemID,
    getEntidadByID,
    getItemById,
    getMedioDePagoIdTipoEspecifico,
    medioDePagoIdTipoEspecifico,
    tipoMedioDePago,
    getTipoMedioDePago,
    user,
    updateCuentaCorriente,
    postTransaccionPago,
    postCrearPago,
    postTransaccion,
    getFechaHoraActualClient,
  } = useContext(ErpContext);

  // Estados principales
  const [openModal, setOpenModal] = useState(false);
  const [openGraficasModal, setOpenGraficasModal] = useState(false);
  const [transaccionItems, setTransaccionItems] = useState([]);
  const [selectedTransaccionId, setSelectedTransaccionId] = useState(null);
  const [selectedCuentaCorriente, setSelectedCuentaCorriente] = useState(null);
  const [selectedTransaccionEntidad, setSelectedTransaccionEntidad] =
    useState(null);
  const [rows, setRows] = useState([]);
  const [selectedUbicacion, setSelectedUbicacion] = useState(null);
  const [today, setToday] = useState(dayjs());
  const [startDate, setStartDate] = useState(today.subtract(30, "day"));
  const [endDate, setEndDate] = useState(today);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0);
  const [minVentas, setMinVentas] = useState(0);
  const [maxVentas, setMaxVentas] = useState(0);
  const [itemsMap, setItemsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortedData, setSortedData] = useState([]);
  const [dniCliente, setDniCliente] = useState("");
  const [searchTextCliente, setSearchTextCliente] = useState("");
  const [filtersActive, setFiltersActive] = useState(false);
  const isFirstRender = useRef(true);
  const [mostrarDeletes, setMostrarDeletes] = useState(false);
  const [idTransaccionSearch, setIdTransaccionSearch] = useState(null);
  const [openModalDevolucion, setOpenModalDevolucion] = useState(false);
  const [cuentasCorrientesTransacciones, setCuentasCorrientesTransacciones] =
    useState([]);

  const [abrirMedioDePago, setAbrirMedioDePago] = useState(false);
  const [precioTotal, setPrecioTotal] = useState(0);

  // Estado para las opciones de items en el Autocomplete
  const [itemsOptions, setItemsOptions] = useState([]);

  // Estados para filtros
  const [filteredData, setFilteredData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchItemText, setSearchItemText] = useState("");
  const [tipoEntidadFilter, setTipoEntidadFilter] = useState(null);
  const [saldoFilter, setSaldoFilter] = useState("todos"); // 'todos', 'positivos', 'negativos', 'cero'
  const [anchorEl, setAnchorEl] = useState(null);
  const [tiposEntidad, setTiposEntidad] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [selectedItemPrice, setSelectedItemPrice] = useState(null);

  // Funciones para manipular fechas
  const increaseDate = () =>
    setStartDate((prev) => (prev ? prev.add(1, "day") : dayjs()));
  const decreaseDate = () =>
    setStartDate((prev) => (prev ? prev.subtract(1, "day") : dayjs()));

  // Estadísticas calculadas
  const [stats, setStats] = useState({
    totalDeudas: 0,
    totalDeudores: 0,
    totalSaldados: 0, // Añadir el total de montoPagado a las estadísticas iniciales
    saldoNeto: 0,
    cantidadDeudas: 0,
    cantidadDeudores: 0,
    cantidadSaldados: 0,
  });

  // Obtener datos al cargar el componente
  useEffect(() => {
    fetchData();
    getUbicacion();
    getNegocio();
  }, []);

  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, 1);
      setLoading(false);
    }
  }, [searchTextCliente]);

  const fetchData = async (
    date = startDate,
    pageParam = 1,
    endDate = today,
    dniCliente = null,
    selectedUbicacion = null,
    valorMin = 0,
    valorMax = 0,
    mostrarDeletes = false,
    idTransaccionSearch = null,
    limit = 100
  ) => {
    try {
      setLoading(true);
      const controller = new AbortController();
      const signal = controller.signal;
      const validDate = date ? date : dayjs();
      let formattedDate = validDate.format("YYYY-MM-DD");
      let formattedDate2 = endDate;
      let idDni = null;
      let idSelectedUbicacion = null;

      // Validar endDate antes de formatear (igual que TablaVentas)
      if (endDate && typeof endDate.format === "function") {
        formattedDate2 = endDate.format("YYYY-MM-DD");
      } else {
        formattedDate2 = formattedDate; // Usar la misma fecha si endDate no es válido
      }

      // ✅ Normalizar fechas con timezone usando getFechaHoraActualClient (igual que TablaVentas)
      const result1 = await getFechaHoraActualClient(formattedDate);
      formattedDate = dayjs(result1.fechaServidor).startOf("day").toDate();

      const result2 = await getFechaHoraActualClient(formattedDate2);
      formattedDate2 = dayjs(result2.fechaServidor).endOf("day").toDate();

      // Validar que las fechas sean válidas antes de enviar
      if (isNaN(formattedDate.getTime()) || isNaN(formattedDate2.getTime())) {
        console.error("Fechas inválidas generadas:", { formattedDate, formattedDate2, result1, result2 });
        throw new Error("Error al procesar fechas: fechas inválidas generadas");
      }

      if (dniCliente != null) {
        idDni = dniCliente.id;
      }
      if (selectedUbicacion != null) {
        idSelectedUbicacion = selectedUbicacion.id;
      }

      const response = await getCuentaCorrienteTransformed(
        1,
        formattedDate,
        pageParam,
        limit,
        { signal },
        formattedDate2,
        idDni,
        idSelectedUbicacion,
        valorMin,
        valorMax,
        mostrarDeletes,
        idTransaccionSearch
      );

      // Verificar si la respuesta tiene la estructura esperada de paginación
      let cuentas, paginationData;
      if (response && typeof response === "object" && response.data) {
        // Respuesta con paginación
        cuentas = response.data;
        paginationData = {
          currentPage: response.currentPage || pageParam,
          totalPages: response.totalPages || 1,
          total: response.total || 0,
          totalMonto: response.totalMonto || 0,
        };
      } else {
        // Respuesta directa (array)
        cuentas = Array.isArray(response) ? response : [];
        paginationData = {
          currentPage: pageParam,
          totalPages: 1,
          total: cuentas.length,
          totalMonto: 0,
        };
      }

      // Me aseguro de tener siempre un array
      const list = Array.isArray(cuentas) ? cuentas : [];

      // Transformo para el DataGrid
      const cuentasTransformadas = await Promise.all(
        list.map(async (cc) => {
          const entidad = cc.entidad || {};
          const tipoEntDesc = entidad.tipoEntidad?.descripcion || "";

          // Garantizo siempre un array de transacciones
          const rawTxs = Array.isArray(cc.transacciones)
            ? cc.transacciones
            : [];
          const txsItems = Array.isArray(cc.transaccionesItems)
            ? cc.transaccionesItems
            : [];

          // Extraer todos los items de todas las transacciones
          const allItems = [];
          const itemsDescriptions = [];

          txsItems.forEach((tx) => {
            if (Array.isArray(tx.TransaccionItems)) {
              tx.TransaccionItems.forEach((item) => {
                if (item.item) {
                  allItems.push({
                    id: item.idItem,
                    descripcion: item.item.descripcion || "Sin descripción",
                    cantidad: item.cantidad || 0,
                    precio: item.precio || 0,
                    montoTotal: item.montoTotal || 0,
                    fecha: tx.fechaHoraCreacion,
                    idTransaccion: tx.id,
                  });
                  itemsDescriptions.push(
                    item.item.descripcion || "Sin descripción"
                  );
                }
              });
            }
          });

          // Eliminar duplicados de descripciones
          const uniqueItemsDescriptions = [...new Set(itemsDescriptions)];

          const txs = rawTxs.sort(
            (a, b) =>
              new Date(b.fechaHoraCreacion).getTime() -
              new Date(a.fechaHoraCreacion).getTime()
          );
          const lastTx = txs[0] || {};
          const lastDateFormatted = lastTx.fechaHoraCreacion
            ? new Intl.DateTimeFormat("es-AR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }).format(new Date(lastTx.fechaHoraCreacion))
            : "";
          const fechaCreacionCC = cc.updatedAt
            ? new Intl.DateTimeFormat("es-AR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }).format(new Date(cc.updatedAt))
            : "";

          // Sumo solo los pagos reales (que saldan deuda)
          // Solo transacciones con operacionParaCuentaCorriente === 'operacionCaja'
          const totalPagado = rawTxs.reduce((sum, tx) => {
            if (tx.operacionParaCuentaCorriente === 'operacionCaja') {
              return sum + Math.abs(tx.montoTotal || 0);
            }
            return sum;
          }, 0);

          return {
            id: cc.idEntidad, // para key / getRowId
            idEntidad: cc.idEntidad,
            tipoEntidad: tipoEntDesc,
            descripcion: entidad.descripcion || "",
            apellido: entidad.apellido || "",
            dniCuitCuil: entidad.dniCuitCuil || "",
            telefono: entidad.telefono || "",
            email: entidad.email || "",
            saldo: cc.saldo,
            fechaCreacionCC: fechaCreacionCC || "",

            // Información de items
            items: allItems,
            itemsCount: allItems.length,
            itemsDescriptions: uniqueItemsDescriptions,
            itemsDescriptionsText: uniqueItemsDescriptions.join(", "),

            // Resumen transacciones
            transCount: txs.length,
            lastTransDate: lastDateFormatted,
            lastTransAmount: lastTx.montoTotal || 0,
            montoPagado: totalPagado,

            // Detalle completo
            transacciones: txs,
            transaccionesItems: txsItems,
            entidadCompleta: entidad,
            cuentaCorrienteCompleta: cc,
          };
        })
      );

      // Actualizar estados de paginación
      setPage(paginationData.currentPage);
      setTotalPages(paginationData.totalPages);
      setTotalCount(paginationData.total);
      setMontoTotalTransacciones(paginationData.totalMonto);

      // Extraer todos los items únicos para el Autocomplete, quedándonos con el más reciente
      const itemsMap = new Map();
      cuentasTransformadas.forEach((cuenta) => {
        if (cuenta.items && Array.isArray(cuenta.items)) {
          cuenta.items.forEach((item) => {
            // Si el item no existe en el mapa o la fecha es más reciente, lo actualizamos
            if (
              !itemsMap.has(item.descripcion) ||
              new Date(item.fecha) >
                new Date(itemsMap.get(item.descripcion).fecha)
            ) {
              itemsMap.set(item.descripcion, {
                id: item.id,
                label: item.descripcion,
                precio: item.precio,
                fecha: item.fecha,
                value: item.descripcion,
              });
            }
          });
        }
      });
      setItemsOptions(Array.from(itemsMap.values()));

      // Extraer tipos de entidad únicos
      const tiposEntidadUnicos = [
        ...new Set(
          cuentasTransformadas
            .map((cuenta) => cuenta.tipoEntidad)
            .filter(Boolean)
        ),
      ];
      setTiposEntidad(
        tiposEntidadUnicos.map((tipo) => ({ label: tipo, value: tipo }))
      );

      // Calcular estadísticas
      const deudas = cuentasTransformadas.filter((row) => row.saldo > 0);
      const deudores = cuentasTransformadas.filter((row) => row.saldo < 0);
      const saldados = cuentasTransformadas.filter((row) => row.saldo === 0);

      const totalDeudas = deudas.reduce((acc, row) => acc + row.saldo, 0);
      const totalDeudores = deudores.reduce(
        (acc, row) => acc + Math.abs(row.saldo),
        0
      );
      // Calcular el total de montoPagado de TODAS las cuentas
      const totalSaldados = cuentasTransformadas.reduce(
        (acc, row) => acc + row.montoPagado,
        0
      );
      // Actualizar el cálculo del saldo neto para incluir el montoPagado
      const saldoNeto = totalDeudores + totalSaldados - totalDeudas;

      setStats({
        totalDeudas,
        totalDeudores,
        totalSaldados, // Añadir el total de montoPagado a las estadísticas
        saldoNeto,
        cantidadDeudas: deudas.length,
        cantidadDeudores: deudores.length,
        cantidadSaldados: saldados.length,
      });

      // Seteo directo la lista transformada
      setCuentasCorrientesTransacciones(cuentasTransformadas);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  // Aplicar filtros cuando cambian
  useEffect(() => {
    if (cuentasCorrientesTransacciones.length > 0) {
      let filtered = [...cuentasCorrientesTransacciones];

      // Filtro de búsqueda por cliente
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        filtered = filtered.filter(
          (row) =>
            (row.descripcion &&
              row.descripcion.toLowerCase().includes(searchLower)) ||
            (row.apellido &&
              row.apellido.toLowerCase().includes(searchLower)) ||
            (row.dniCuitCuil &&
              row.dniCuitCuil.toLowerCase().includes(searchLower)) ||
            (row.email && row.email.toLowerCase().includes(searchLower))
        );
      }

      // Filtro de búsqueda por item
      if (searchItemText) {
        const searchLower = searchItemText.toLowerCase();
        filtered = filtered.filter(
          (row) =>
            row.itemsDescriptionsText &&
            row.itemsDescriptionsText.toLowerCase().includes(searchLower)
        );
      }

      // Filtro por tipo de entidad
      if (tipoEntidadFilter) {
        filtered = filtered.filter(
          (row) => row.tipoEntidad === tipoEntidadFilter.value
        );
      }

      // Filtro por saldo
      if (saldoFilter === "positivos") {
        filtered = filtered.filter((row) => row.saldo > 0);
      } else if (saldoFilter === "negativos") {
        filtered = filtered.filter((row) => row.saldo < 0);
      } else if (saldoFilter === "cero") {
        filtered = filtered.filter((row) => row.saldo === 0);
      }

      setFilteredData(filtered);

      // Verificar si hay filtros activos
      const hasActiveFilters =
        searchText !== "" ||
        searchItemText !== "" ||
        tipoEntidadFilter !== null ||
        saldoFilter !== "todos";

      setFiltersActive(hasActiveFilters);
    }
  }, [
    cuentasCorrientesTransacciones,
    searchText,
    searchItemText,
    tipoEntidadFilter,
    saldoFilter,
  ]);

  // Actualizar estadísticas cuando cambian los datos filtrados
  useEffect(() => {
    if (filteredData.length > 0) {
      // Calcular estadísticas basadas en los datos filtrados
      const deudas = filteredData.filter((row) => row.saldo > 0);
      const deudores = filteredData.filter((row) => row.saldo < 0);
      const saldados = filteredData.filter((row) => row.saldo === 0);

      const totalDeudas = deudas.reduce((acc, row) => acc + row.saldo, 0);
      const totalDeudores = deudores.reduce(
        (acc, row) => acc + Math.abs(row.saldo),
        0
      );
      // Calcular el total de montoPagado de TODAS las cuentas filtradas
      const totalSaldados = filteredData.reduce(
        (acc, row) => acc + row.montoPagado,
        0
      );
      // Actualizar el cálculo del saldo neto para incluir el montoPagado
      const saldoNeto = totalDeudores + totalSaldados - totalDeudas;

      setStats({
        totalDeudas,
        totalDeudores,
        totalSaldados, // Añadir el total de montoPagado a las estadísticas
        saldoNeto,
        cantidadDeudas: deudas.length,
        cantidadDeudores: deudores.length,
        cantidadSaldados: saldados.length,
      });
    }
  }, [filteredData]);

  // Datos para el gráfico de torta
  const pieData = [
    {
      name: "Deudas por cobrar",
      value: stats.cantidadDeudores,
      color: theme.palette.error.main,
    },
    {
      name: "Deudas a proveedores",
      value: stats.cantidadDeudas,
      color: theme.palette.warning.main,
    },
    {
      name: "Deudas saldadas",
      value: stats.cantidadSaldados,
      color: theme.palette.success.main,
    },
  ];

  // Datos para el gráfico de barras
  const barData = [
    {
      name: "Deudas",
      monto: stats.totalDeudas,
      color: theme.palette.warning.main,
    },
    {
      name: "Deudores",
      monto: stats.totalDeudores,
      color: theme.palette.error.main,
    },
    {
      name: "Total Pagado",
      monto: stats.totalSaldados,
      color: theme.palette.success.main,
    },
  ];

  // Datos para el gráfico de líneas (simulados para demostración)
  const lineData = Array.from({ length: 7 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    const formattedDate = date.toLocaleDateString("es-AR", {
      day: "2-digit",
      month: "2-digit",
    });

    return {
      fecha: formattedDate,
      deudas: Math.floor(stats.totalDeudas * (0.9 + Math.random() * 0.2)),
      deudores: Math.floor(stats.totalDeudores * (0.9 + Math.random() * 0.2)),
      pagado: Math.floor(stats.totalSaldados * (0.9 + Math.random() * 0.2)),
    };
  });

  // Funciones para manejar filtros
  const buttonFiltrado = () => {
    fetchData(
      startDate,
      1,
      endDate,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      idTransaccionSearch
    );
  };

  const handleMinChange = (e) => {
    const newMin = Number(e.target.value);
    setMinVentas(newMin);
  };

  const handleMaxChange = (e) => {
    const newMax = Number(e.target.value);
    setMaxVentas(newMax);
  };

  // Función para abrir el modal de detalles
  const handleOpenModal = (cuentaCorriente) => {
    setSelectedCuentaCorriente(cuentaCorriente);
    setOpenModal(true);
  };

  // Función para cerrar el modal de detalles
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCuentaCorriente(null);
  };

  // Función para abrir el modal de gráficas
  const handleOpenGraficasModal = () => {
    setOpenGraficasModal(true);
  };

  // Función para cerrar el modal de gráficas
  const handleCloseGraficasModal = () => {
    setOpenGraficasModal(false);
  };

  // Función para cerrar el modal de gráficas
  const handleCloseMedioDePago = () => {
    setAbrirMedioDePago(false);
  };

  const handleOpenMediosDePago = (datos) => {
    console.log("handleOpenMediosDePago datos: ", datos);
    setPrecioTotal(Math.abs(datos.saldo)); // Usar valor absoluto del saldo
    setSelectedCuentaCorriente(datos); // Guardar los datos de la cuenta
    setAbrirMedioDePago(true);
  };

  const handleConfirmarPago = async (
    mediosDePago,
    montoTotalMediosPago,
    transaccionSeleccionada,
    descripcion,
    selectedUbicacionModal = null
  ) => {
    // Calcular si es pago completo o parcial
    const esPagoCompleto =
      Math.abs(montoTotalMediosPago) >=
      Math.abs(transaccionSeleccionada?.saldo);

    const descripcionFinal = descripcion || "Pago cuenta corriente";

    // Determinar idUbicacion e idNegocio
    // Prioridad: selectedUbicacionModal > ubicación de transacción más reciente > fallback (1)
    let idUbicacion = 1; // Fallback por defecto
    let idNegocio = 1;   // Fallback por defecto

    if (selectedUbicacionModal && selectedUbicacionModal.id) {
      // Usar la ubicación seleccionada en el modal
      idUbicacion = selectedUbicacionModal.id;
      // Usar el idNegocio directamente del objeto ubicación si está disponible
      if (selectedUbicacionModal.idNegocio) {
        idNegocio = selectedUbicacionModal.idNegocio;
      } else {
        // Si la ubicación no tiene idNegocio, buscar en negocios o usar default
        const negocioDefault = negocios?.find((n) => n.id === 1);
        idNegocio = negocioDefault ? negocioDefault.id : 1;
      }
    } else if (transaccionSeleccionada.transacciones && transaccionSeleccionada.transacciones.length > 0) {
      // Usar la ubicación de la transacción más reciente
      const ultimaTransaccion = transaccionSeleccionada.transacciones[0]; // Ya está ordenada por fecha DESC
      if (ultimaTransaccion.idUbicacion) {
        idUbicacion = ultimaTransaccion.idUbicacion;
      }
      if (ultimaTransaccion.idNegocio) {
        idNegocio = ultimaTransaccion.idNegocio;
      }
    }
    // Si no hay ninguna, ya están en los fallbacks (1, 1)
    

let transaccionResponse;

    if (transaccionSeleccionada.saldo <= 0) {
        transaccionResponse = await postTransaccion(
      montoTotalMediosPago, //Monto todal de la transaccion
      4, // Ingreso
      transaccionSeleccionada.id, //idEntidad
      null, // no es necesario enviar subcategoria
      descripcionFinal,
      user.id,
      idUbicacion, // ubicación seleccionada o determinada
      idNegocio, // negocio asociado o default
      0, // descuento
      0, // porcentaje descuento
      null, // transaccionAsociada
      null, // fecha
      true, // afectaCuentaCorriente
      null, // clientOS
      'operacionCaja' // operacionParaCuentaCorriente: usar operacionCaja para pagos desde TablaCuentaCorriente
    );

    console.log("transaccionSeleccionada: ", transaccionSeleccionada);

    console.log("montoTotalMediosPago: ", montoTotalMediosPago);

    console.log(transaccionResponse);

    const idTransaccion = transaccionResponse.data.id;

    for (const it of mediosDePago) {
      // Crear pago
      const transaccionCrearPago = await postCrearPago(
        1, //Moneda va por default 1 que es peso hardcoded
        1, // cotización
        it.id, //Medio de pago id
        it.monto
      );
      console.log("it.monto: ", it.monto);
      const idTransaccionCrearPago = transaccionCrearPago.data.id;
      await postTransaccionPago(idTransaccion, idTransaccionCrearPago);
    }
    await updateCuentaCorriente(
      transaccionSeleccionada.id,
      montoTotalMediosPago,
      4,
      true // usarOperacionCaja = true: usar operacionCaja en lugar de operacionCuentaCorriente
    ); // Ingreso: cliente paga su deuda

    }

    else {
              transaccionResponse = await postTransaccion(
      montoTotalMediosPago, //Monto todal de la transaccion
      5, // Egreso
      transaccionSeleccionada.id, //idEntidad
      null, // no es necesario enviar subcategoria
      descripcionFinal,
      user.id,
      idUbicacion, // ubicación seleccionada o determinada
      idNegocio, // negocio asociado o default
      0, // descuento
      0, // porcentaje descuento
      null, // transaccionAsociada
      null, // fecha
      true, // afectaCuentaCorriente
      null, // clientOS
      'operacionCaja' // operacionParaCuentaCorriente: usar operacionCaja para pagos desde TablaCuentaCorriente
    );

    console.log("transaccionSeleccionada: ", transaccionSeleccionada);

    console.log("montoTotalMediosPago: ", montoTotalMediosPago);

    console.log(transaccionResponse);

    const idTransaccion = transaccionResponse.data.id;

    for (const it of mediosDePago) {
      // Crear pago
      const transaccionCrearPago = await postCrearPago(
        1, //Moneda va por default 1 que es peso hardcoded
        1, // cotización
        it.id, //Medio de pago id
        it.monto
      );
      console.log("it.monto: ", it.monto);
      const idTransaccionCrearPago = transaccionCrearPago.data.id;
      await postTransaccionPago(idTransaccion, idTransaccionCrearPago);
    }
    await updateCuentaCorriente(
      transaccionSeleccionada.id,
      montoTotalMediosPago,
      5,
      true // usarOperacionCaja = true: usar operacionCaja en lugar de operacionCuentaCorriente
    ); // Egreso: pagamos deuda al proveedor

    }
   
    setAbrirMedioDePago(false);

    await fetchData(
      startDate,
      page,
      endDate,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      idTransaccionSearch
    );

    // Opcional: refrescar datos después del pago
    // fetchData()
  };

  // Función para resetear filtros
  const resetFilters = () => {
    setSearchText("");
    setSearchItemText("");
    setTipoEntidadFilter(null);
    setSaldoFilter("todos");
    setShowAdvancedFilters(false);
    setDniCliente(null);
    setSelectedUbicacion(null);
    setSearchTextCliente("");
    setMinVentas(0);
    setMaxVentas(0);
    setMostrarDeletes(false);
    setIdTransaccionSearch("");
    setSelectedItemPrice(null);

    // Mantener solo la fecha de inicio como hoy
    const today = dayjs();
    setStartDate(today.subtract(30, "day"));
    setEndDate(today);

    // Refrescar datos con filtros reseteados
    fetchData();
  };

  // Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(value);
  };

  // Definición de columnas para la tabla
  const columns = [
    {
      field: "idEntidad",
      headerName: "ID",
      width: 70,
      flex: 0.3,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          ID
        </Typography>
      ),
    },
    {
      field: "fechaCreacionCC",
      headerName: "Fecha",
      width: 70,
      flex: 1,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Fecha
        </Typography>
      ),
    },
    {
      field: "tipoEntidad",
      headerName: "Tipo",
      width: 150,
      flex: 0.8,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Tipo Entidad
        </Typography>
      ),
      renderCell: (params) => {
        const val = params.value || "";
        return (
          <Chip
            icon={
              val.includes("Cliente") ? (
                <PersonIcon fontSize="small" />
              ) : (
                <BusinessIcon fontSize="small" />
              )
            }
            label={val}
            size="small"
            sx={{
              bgcolor: val.includes("Cliente")
                ? alpha(theme.palette.info.main, 0.1)
                : alpha(theme.palette.secondary.main, 0.1),
              color: val.includes("Cliente")
                ? theme.palette.info.main
                : theme.palette.secondary.main,
              fontWeight: "bold",
            }}
          />
        );
      },
    },
    {
      field: "descripcion",
      headerName: "Entidad",
      width: 180,
      flex: 1,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Entidad
        </Typography>
      ),
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value || ""} {params.row.apellido || ""}
          </Typography>
        </Box>
      ),
    },
    {
      field: "dniCuitCuil",
      headerName: "DNI/CUIT/CUIL",
      width: 150,
      flex: 0.8,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          DNI/CUIT/CUIL
        </Typography>
      ),
    },
    {
      field: "itemsDescriptions",
      headerName: "Items",
      width: 200,
      flex: 1.2,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Items
        </Typography>
      ),
      renderCell: (params) => {
        const items = params.value || [];
        const itemCount = params.row.itemsCount || 0;

        if (itemCount === 0) {
          return <Typography variant="body2">Sin items</Typography>;
        }

        if (items.length === 1) {
          return (
            <Tooltip title={items[0]}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ShoppingCartIcon
                  fontSize="small"
                  sx={{ color: alpha("#5b5b5f", 0.7) }}
                />
                <Typography variant="body2" noWrap>
                  {items[0]}
                </Typography>
              </Box>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={items.join(", ")}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Badge badgeContent={itemCount} color="primary">
                <ShoppingCartIcon
                  fontSize="small"
                  sx={{ color: alpha("#5b5b5f", 0.7) }}
                />
              </Badge>
              <Typography variant="body2" noWrap>
                {items.length > 0 ? `${items[0]}, ...` : "Sin items"}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "saldo",
      headerName: "Saldo",
      type: "number",
      width: 150,
      flex: 0.8,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Saldo
        </Typography>
      ),
      renderCell: (params) => {
        const val = params.value ?? 0;
        let color, icon;
        if (val === 0) {
          color = theme.palette.success.main;
          icon = <BalanceIcon fontSize="small" />;
        } else if (val > 0) {
          color = theme.palette.warning.main;
          icon = <TrendingUpIcon fontSize="small" />;
        } else {
          color = theme.palette.error.main;
          icon = <TrendingDownIcon fontSize="small" />;
        }
        return (
          <Chip
            icon={icon}
            label={`$${val.toLocaleString("es-AR")}`}
            size="small"
            sx={{
              bgcolor: alpha(color, 0.1),
              color,
              fontWeight: "bold",
              "& .MuiChip-icon": { color },
            }}
          />
        );
      },
    },
    {
      field: "transCount",
      headerName: "Pagos",
      width: 120,
      flex: 0.6,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Pagos
        </Typography>
      ),
      renderCell: (params) => (
        <Chip
          label={params.value || 0}
          size="small"
          color="info"
          variant="outlined"
        />
      ),
    },
    {
      field: "montoPagado",
      headerName: "Total Pagado",
      width: 120,
      flex: 0.8,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Total Pagado
        </Typography>
      ),
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          {formatCurrency(params.value || 0)}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 120,
      flex: 0.5,
      headerAlign: "center",
      sortable: false,
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Acciones
        </Typography>
      ),
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Ver detalles">
              <IconButton
                size="small"
                onClick={() => handleOpenModal(params.row)}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Pagar">
              <IconButton
                size="small"
                onClick={() => handleOpenMediosDePago(params.row)}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <PaymentsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Más acciones">
              <IconButton
                size="small"
                onClick={(event) => setAnchorEl(event.currentTarget)}
                sx={{
                  bgcolor: alpha(theme.palette.secondary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.secondary.main, 0.2),
                  },
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
            >
              <MenuItem onClick={() => setAnchorEl(null)}>
                <ReceiptIcon fontSize="small" sx={{ mr: 1 }} />
                Ver transacciones
              </MenuItem>
              <MenuItem onClick={() => setAnchorEl(null)}>
                <PrintIcon fontSize="small" sx={{ mr: 1 }} />
                Imprimir
              </MenuItem>
              <MenuItem onClick={() => setAnchorEl(null)}>
                <FileDownloadIcon fontSize="small" sx={{ mr: 1 }} />
                Exportar
              </MenuItem>
            </Menu>
          </Box>
        );
      },
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 2 }}>
        {/* Tarjetas de resumen */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <AccountBalanceWalletIcon
                    sx={{ color: theme.palette.primary.main, mr: 1 }}
                  />
                  <Typography variant="h6">Balance General</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Saldo Neto:
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color={stats.saldoNeto >= 0 ? "success.main" : "error.main"}
                  >
                    {formatCurrency(stats.saldoNeto)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Total Deudas:
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color="warning.main"
                  >
                    {formatCurrency(stats.totalDeudas)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    Total a Cobrar:
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color="error.main"
                  >
                    {formatCurrency(stats.totalDeudores)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Total Pagado:
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color="success.main"
                  >
                    {formatCurrency(stats.totalSaldados)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <TrendingUpIcon
                    sx={{ color: theme.palette.warning.main, mr: 1 }}
                  />
                  <Typography variant="h6">Cuentas por pagar</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Monto Total:
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color="warning.main"
                  >
                    {formatCurrency(stats.totalDeudas)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Cantidad:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {stats.cantidadDeudas} deudas
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    Promedio:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {stats.cantidadDeudas > 0
                      ? formatCurrency(stats.totalDeudas / stats.cantidadDeudas)
                      : formatCurrency(0)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <TrendingDownIcon
                    sx={{ color: theme.palette.error.main, mr: 1 }}
                  />
                  <Typography variant="h6">Cuentas por Cobrar</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Monto Total:
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color="error.main"
                  >
                    {formatCurrency(stats.totalDeudores)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Cantidad:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {stats.cantidadDeudores} clientes
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="body2" color="text.secondary">
                    Promedio:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {stats.cantidadDeudores > 0
                      ? formatCurrency(
                          stats.totalDeudores / stats.cantidadDeudores
                        )
                      : formatCurrency(0)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Gráficos */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" align="center" gutterBottom>
                Distribución de Cuentas
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                      labelLine={false}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      formatter={(value, name) => [`${value} cuentas`, name]}
                      contentStyle={{
                        borderRadius: 8,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" align="center" gutterBottom>
                Comparación de Montos
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={barData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" />
                    <YAxis
                      tickFormatter={(value) =>
                        `$${value.toLocaleString("es-AR", {
                          notation: "compact",
                        })}`
                      }
                    />
                    <RechartsTooltip
                      formatter={(value) => [
                        `$${value.toLocaleString("es-AR")}`,
                        "",
                      ]}
                      contentStyle={{
                        borderRadius: 8,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Bar
                      dataKey="monto"
                      radius={[8, 8, 0, 0]}
                      barSize={60}
                      fill={(entry, index) => barData[index].color}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" align="center" gutterBottom>
                Evolución Semanal
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={lineData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="fecha" />
                    <YAxis
                      tickFormatter={(value) =>
                        `$${value.toLocaleString("es-AR", {
                          notation: "compact",
                        })}`
                      }
                    />
                    <RechartsTooltip
                      formatter={(value) => [
                        `$${value.toLocaleString("es-AR")}`,
                        "",
                      ]}
                      contentStyle={{
                        borderRadius: 8,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend />
                    <defs>
                      <linearGradient
                        id="colorDeudas"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={theme.palette.warning.main}
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor={theme.palette.warning.main}
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="colorDeudores"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={theme.palette.error.main}
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor={theme.palette.error.main}
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                      <linearGradient
                        id="colorPagado"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor={theme.palette.success.main}
                          stopOpacity={0.8}
                        />
                        <stop
                          offset="95%"
                          stopColor={theme.palette.success.main}
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="deudas"
                      stroke={theme.palette.warning.main}
                      fillOpacity={1}
                      fill="url(#colorDeudas)"
                      name="Deudas"
                    />
                    <Area
                      type="monotone"
                      dataKey="deudores"
                      stroke={theme.palette.error.main}
                      fillOpacity={1}
                      fill="url(#colorDeudores)"
                      name="Deudores"
                    />
                    <Area
                      type="monotone"
                      dataKey="pagado"
                      stroke={theme.palette.success.main}
                      fillOpacity={1}
                      fill="url(#colorPagado)"
                      name="Total Pagado"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Filtros */}
        <Paper
          className="diseñoFiltroTransaccionesVenta"
          elevation={3}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {/* Fila principal con búsqueda de items y botones */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: showAdvancedFilters ? 2 : 0,
            }}
          >
            {/* Búsqueda de items */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                flex: 1,
                maxWidth: 600,
              }}
            >
              <Box sx={{ position: "relative", flex: 1, maxWidth: 400 }}>
                <Autocomplete
                  size="small"
                  options={itemsOptions}
                  getOptionLabel={(option) => option.label || option}
                  value={searchItemText ? { label: searchItemText } : null}
                  onChange={(event, newValue) => {
                    setSearchItemText(newValue ? newValue.label : "");
                    setSelectedItemPrice(newValue ? newValue.precio : null);
                  }}
                  onInputChange={(event, newInputValue) => {
                    setSearchItemText(newInputValue);
                    if (!newInputValue) {
                      setSelectedItemPrice(null);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Buscar por item"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <ShoppingCartIcon
                              fontSize="small"
                              sx={{ color: theme.palette.primary.main }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.secondary.main,
                          },
                        },
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Typography variant="body2">{option.label}</Typography>
                        {option.precio && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            fontWeight="medium"
                          >
                            {formatCurrency(option.precio)}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  )}
                  freeSolo
                />
                {searchItemText && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchItemText("");
                      setSelectedItemPrice(null);
                    }}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: 8,
                      zIndex: 2,
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      "&:hover": {
                        bgcolor: alpha(theme.palette.error.main, 0.2),
                      },
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <TextField
                label="Precio original"
                size="small"
                value={
                  selectedItemPrice ? formatCurrency(selectedItemPrice) : ""
                }
                InputProps={{
                  readOnly: true,
                  startAdornment: selectedItemPrice ? null : (
                    <InputAdornment position="start">
                      <ShoppingCartIcon fontSize="small" color="disabled" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 180,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.grey[100], 0.5),
                  },
                }}
              />
            </Box>

            {/* Botones de acción */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Button
                variant="contained"
                size="medium"
                onClick={buttonFiltrado}
                startIcon={<SearchIcon />}
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  textTransform: "none",
                  padding: "10px 20px",
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "&:hover": {
                    backgroundColor: theme.palette.secondary.main,
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Buscar
              </Button>

              <Button
                variant="contained"
                size="medium"
                onClick={handleOpenGraficasModal}
                startIcon={<BarChartIcon />}
                sx={{
                  backgroundColor: theme.palette.secondary.main,
                  textTransform: "none",
                  padding: "10px 20px",
                  borderRadius: 2,
                  fontWeight: 600,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.secondary.main, 0.8),
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                Gráficas
              </Button>

              <Button
                variant="outlined"
                size="medium"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  textTransform: "none",
                  padding: "10px 20px",
                  borderRadius: 2,
                  fontWeight: 600,
                  borderWidth: 2,
                  "&:hover": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {showAdvancedFilters ? "Ocultar filtros" : "Mostrar filtros"}
              </Button>
            </Box>
          </Box>

          {/* Filtros avanzados */}
          {showAdvancedFilters && (
            <Box
              sx={{
                borderRadius: 2,
                bgcolor: alpha(theme.palette.grey[50], 0.8),
                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "stretch",
                  justifyContent: "space-between",
                  width: "100%",
                  flexWrap: "nowrap",
                }}
              >
                {/* Fechas */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "white",
                    border: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.2
                    )}`,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <IconButton
                      onClick={decreaseDate}
                      color="primary"
                      size="small"
                      sx={{ p: 0.3 }}
                    >
                      <ArrowBackIcon fontSize="small" />
                    </IconButton>
                    <DatePicker
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: {
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                              fontSize: "0.75rem",
                            },
                            "& .MuiInputLabel-root": {
                              fontSize: "0.75rem",
                            },
                          },
                        },
                      }}
                    />
                    <DatePicker
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      format="DD/MM/YYYY"
                      slotProps={{
                        textField: {
                          size: "small",
                          sx: {
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 1,
                              fontSize: "0.75rem",
                            },
                            "& .MuiInputLabel-root": {
                              fontSize: "0.75rem",
                            },
                          },
                        },
                      }}
                    />
                    <IconButton
                      onClick={increaseDate}
                      color="primary"
                      size="small"
                      sx={{ p: 0.3 }}
                    >
                      <ArrowForwardIcon fontSize="small" />
                    </IconButton>
                  </LocalizationProvider>
                </Box>

                {/* Montos */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "white",
                    border: `1px solid ${alpha(
                      theme.palette.warning.main,
                      0.3
                    )}`,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <TextField
                    label="Min"
                    type="number"
                    size="small"
                    value={minVentas}
                    onChange={handleMinChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        fontSize: "0.75rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                  <TextField
                    label="Max"
                    type="number"
                    size="small"
                    value={maxVentas}
                    onChange={handleMaxChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        fontSize: "0.75rem",
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                </Box>

                {/* Cliente */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "white",
                    border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Autocomplete
                    size="small"
                    disablePortal
                    options={entidadesClientesTresLetras.map((it) => ({
                      label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
                      ...it,
                    }))}
                    value={dniCliente}
                    onChange={(event, newValue) =>
                      setDniCliente(newValue || null)
                    }
                    onInputChange={(event, newInputValue) =>
                      setSearchTextCliente(newInputValue)
                    }
                    sx={{ width: "100%" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Buscar Entidad"
                        variant="outlined"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1,
                            fontSize: "0.75rem",
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  />
                </Box>

                {/* Filtros de Estado */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: "white",
                    border: `1px solid ${alpha(
                      theme.palette.success.main,
                      0.3
                    )}`,
                    flex: 2,
                    minWidth: 0,
                  }}
                >
                  <Autocomplete
                    size="small"
                    options={tiposEntidad}
                    value={tipoEntidadFilter}
                    onChange={(event, newValue) =>
                      setTipoEntidadFilter(newValue)
                    }
                    sx={{ width: "100%" }}
                    label="Tipo entidad"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Tipo entidad"
                        size="small"
                        sx={{
                          width: "100%",
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1,
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    )}
                  />

                  <TextField
                    select
                    placeholder="Estado saldo"
                    value={saldoFilter}
                    onChange={(e) => setSaldoFilter(e.target.value)}
                    size="small"
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        fontSize: "0.75rem",
                      },
                    }}
                  >
                    <MenuItem value="todos">Todos</MenuItem>
                    <MenuItem value="positivos">Deudas</MenuItem>
                    <MenuItem value="negativos">Deudores</MenuItem>
                    <MenuItem value="cero">Saldados</MenuItem>
                  </TextField>
                </Box>

                {/* Botón de Reset */}
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={resetFilters}
                  sx={{
                    textTransform: "none",
                    padding: "6px 12px",
                    borderRadius: 2,
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    minHeight: 36,
                    boxShadow: "0 2px 8px rgba(244, 67, 54, 0.3)",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: "0 4px 12px rgba(244, 67, 54, 0.4)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Tabla */}
        <Paper sx={{ height: 500, width: "100%" }}>
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            rows={
              filteredData.length > 0
                ? filteredData
                : cuentasCorrientesTransacciones
            }
            columns={columns}
            getRowId={(row) => row.idEntidad}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            components={{
              Toolbar: GridToolbar,
            }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            className="tablaVentasDataGrid"
            sx={{
              width: "100%",
              border: 0,
              "& .MuiDataGrid-footerContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "white !important",
                lineHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                color: "white !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiInputBase-root": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiTablePagination-root": {
                color: "black !important",
              },
              "& .MuiSelect-icon": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": {
                  backgroundColor: "#F7F7F7",
                },
                "&:hover": {
                  backgroundColor: "#E3EAFD !important",
                  transition: "background 0.3s ease",
                },
              },
              "& .row-devolucion": {
                backgroundColor: alpha("#9e9e9e !important", 0.05),
                "&:hover": {
                  backgroundColor: alpha("#9e9e9e !important", 0.1),
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                lineHeight: "none !important",
                height: "10px !imporant",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "2px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
            }}
            getRowClassName={(params) => {
              if (params.row.saldo === 0) return "row-saldado";
              if (params.row.saldo > 0) return "row-deuda";
              return "row-deudor";
            }}
            loading={loading}
          />
        </Paper>

        {/* Contenedor del footer (paginación) */}
        <Box sx={{ flexShrink: 0, p: 1 }}>
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={montoTotalTransacciones}
            vieneDe={"cuentaCorriente"}
            onPageChange={(newPage) => {
              setPage(newPage);
              fetchData(
                startDate,
                newPage,
                endDate,
                dniCliente,
                selectedUbicacion,
                minVentas,
                maxVentas,
                mostrarDeletes,
                idTransaccionSearch
              );
            }}
          />
        </Box>

        {/* Modal de detalles */}
        <ModalDetalleCuentaCorriente
          open={openModal}
          handleClose={handleCloseModal}
          cuentaCorriente={selectedCuentaCorriente}
        />

        {/* Modal de gráficas */}
        <GraficasCuentaCorrienteModal
          open={openGraficasModal}
          handleClose={handleCloseGraficasModal}
          data={
            filteredData.length > 0
              ? filteredData
              : cuentasCorrientesTransacciones
          }
        />

        <ModalMedioDePago
          open={abrirMedioDePago}
          handleClose={handleCloseMedioDePago}
          precioTotal={precioTotal}
          transaccionSeleccionada={selectedCuentaCorriente}
          onConfirmarPago={handleConfirmarPago}
          isCuentaCorriente={true}
        />
      </Box>
    </ThemeProvider>
  );
};

export default CuentasCorrientesTable;
